import Vue from 'vue';
import VueRouter from 'vue-router';

import HomeView from '../views/HomeView.vue';
import JobsView from '../views/JobsView.vue';
import JobsScrollMenu from '../components/scroll-menu/jobs-overview.vue';
import JobDetailsView from '../views/JobDetailsView.vue';
import JobDetailsPreview from '../views/JobDetailsPreview.vue';
import JobDetailsScrollMenu from '../components/scroll-menu/job-details.vue';
import ImprintView from '../views/ImprintView.vue';
import DataPrivacyView from '../views/PrivacyPolicyView.vue';
import ContentSite from '../views/ContentSite.vue';
import ApplyForm from '../views/ApplyForm.vue';
import ResumeForm from '../views/ApplicationResumeForm.vue';
import JobalertUpdate from '../views/JobalertUpdate.vue';
import RequestInformationView from '../views/RequestInformationView.vue';
import JobStatusView from '../views/JobStatusView.vue';
import AccountConfirmView from '../views/AccountConfirmView.vue';
import UpdateAppointmentView from '../views/UpdateAppointmentView.vue';
import SelectInterviewView from '../views/SelectInterviewView.vue';
import NotFound from '../views/NotFound.vue';
import TrackingService from '../services/TrackingService.js';
import { updateNoIndexTag } from '../helpers/NoIndexHelper.js';
import store from '../store';

Vue.use(VueRouter);

const routes = [
  {
    path: '/:slug',
    name: 'home',
    meta: { layout: 'no-filter-layout', heroClass: 'hero--full-height' },
    component: HomeView
  },

  {
    path: '/',
    name: 'default',
    meta: { layout: 'no-filter-layout', heroClass: 'hero--full-height' },
    component: HomeView
  },

  // {
  //   path: '/',
  //   redirect: 'hidden-professionals/jobs'
  // },
  {
    path: '/:slug/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/AboutView.vue')
  },
  {
    path: '/:slug/jobs',
    name: 'jobs',
    meta: { hasScrollMenu: true },
    components: {
      default: JobsView,
      scrollMenu: JobsScrollMenu
    }
  },
  {
    path: '/:slug/jobs/:internalToken',
    name: 'jobsInternal',
    meta: { hasScrollMenu: true, noindex: true },
    components: {
      default: JobsView,
      scrollMenu: JobsScrollMenu
    }
  },

  {
    path: '/:slug/job/preview/:jobId/:token',
    name: 'jobPreview',
    meta: {
      layout: 'job-details-layout',
      hasScrollMenu: false,
      noindex: true,
      noHeader: true,
      noFooter: true,
      noTracking: true
    },
    components: {
      default: JobDetailsPreview,
      scrollMenu: JobDetailsScrollMenu
    }
  },

  {
    path: '/:slug/job/:jobId',
    name: 'jobDetails',
    meta: {
      layout: 'job-details-layout',
      hasScrollMenu: false,
      customPageTitle: true
    },
    components: {
      default: JobDetailsView,
      scrollMenu: JobDetailsScrollMenu
    }
  },

  {
    path: '/:slug/job/:jobId/:internalToken',
    name: 'jobDetails',
    meta: {
      layout: 'job-details-layout',
      hasScrollMenu: false,
      noindex: true,
      customPageTitle: true
    },
    components: {
      default: JobDetailsView,
      scrollMenu: JobDetailsScrollMenu
    }
  },

  {
    path: '/:slug/imprint',
    name: 'imprint',
    meta: { layout: 'no-filter-layout', noindex: true },
    component: ImprintView
  },

  {
    path: '/:slug/data-privacy',
    name: 'privacyPolicy',
    meta: { layout: 'no-filter-layout', noindex: true },
    component: DataPrivacyView
  },

  {
    path: '/:slug/site/:siteName',
    name: 'contentSite',
    meta: { layout: 'no-filter-layout' },
    component: ContentSite
  },

  {
    path: '/:slug/jobalert/confirmed',
    name: 'jobalertConfirmation',
    meta: { layout: 'no-filter-layout' },

    beforeEnter: (to, from, next) => {
      next({
        name: 'contentSite',
        params: { siteName: 'jobalert-confirmation', noindex: true }
      });
    },

    component: ContentSite
  },

  {
    path: '/:slug/jobalert/unsubscribed',
    name: 'jobalertUnsubscribed',
    meta: { layout: 'no-filter-layout' },

    beforeEnter: (to, from, next) => {
      next({
        name: 'contentSite',
        params: { siteName: 'jobalert-unsubscribed', noindex: true }
      });
    },

    component: ContentSite
  },

  {
    path: '/:slug/jobalert/confirm/:jobalertId/:jobalertToken',
    name: 'confirmJobalert',
    meta: { layout: 'no-filter-layout', noindex: true },
    component: JobalertUpdate
  },

  {
    path: '/:slug/jobalert/unsubscribe/:jobalertId/:jobalertToken',
    name: 'unsubscribeJobalert',
    meta: { layout: 'no-filter-layout', noindex: true },
    component: JobalertUpdate
  },

  {
    path: '/:slug/resume/:applicationId',
    name: 'resume',
    meta: { layout: 'apply-form-layout', noindex: true },
    component: ResumeForm
  },

  {
    path: '/:slug/apply/:jobofferId',
    name: 'apply',
    meta: { layout: 'apply-form-layout', noindex: true, customPageTitle: true },
    component: ApplyForm
  },

  {
    path: '/:slug/apply/:jobofferId/:internalToken',
    name: 'apply',
    meta: { layout: 'apply-form-layout', noindex: true, customPageTitle: true },
    component: ApplyForm
  },
  {
    path: '/:slug/confirm/:token',
    name: 'confirmAccount',
    meta: { layout: 'apply-form-layout', noindex: true },
    component: AccountConfirmView
  },
  {
    path: '/:slug/requestInformation/:applicationId/:actionId/:token',
    name: 'requestInformation',
    meta: { layout: 'apply-form-layout', noindex: true },
    component: RequestInformationView
  },

  {
    path: '/:slug/appointment/accept/:applicationId/:interviewToken/:authToken',
    name: 'acceptAppointment',
    meta: { layout: 'apply-form-layout', noindex: true },
    props: () => ({ type: 0 }),
    component: UpdateAppointmentView
  },

  {
    path: '/:slug/appointment/reject/:applicationId/:interviewToken/:authToken',
    name: 'rejectAppointment',
    meta: { layout: 'apply-form-layout', noindex: true },
    props: () => ({ type: 1 }),
    component: UpdateAppointmentView
  },

  {
    path: '/:slug/appointment/deny/:applicationId/:interviewToken/:authToken',
    name: 'denyAppointment',
    meta: { layout: 'apply-form-layout', noindex: true },
    props: () => ({ type: 2 }),
    component: UpdateAppointmentView
  },

  {
    path: '/:slug/appointment/select/:applicationId/:authToken',
    name: 'selectInterview',
    meta: { layout: 'apply-form-layout', noindex: true },
    component: SelectInterviewView
  },

  {
    path: '/:slug/jobstatus/:jobId/:identifier/:token',
    name: 'jobStatus',
    meta: { layout: 'apply-form-layout', noindex: true },
    component: JobStatusView
  },

  {
    path: '/:slug/cockpit',
    name: 'cockpit',
    meta: { layout: 'no-filter-layout', noindex: true, requiresAuth: true },
    component: () =>
      import(/* webpackChunkName: "cockpit" */ '../views/CockpitView.vue')
  },

  {
    path: '/:slug/cockpit/profile',
    name: 'cockpitProfile',
    meta: { layout: 'no-filter-layout', noindex: true, requiresAuth: true },
    component: () =>
      import(/* webpackChunkName: "cockpit" */ '../views/ProfileView.vue')
  },

  {
    path: '/:slug/not-found',
    name: 'fileNotFound',
    meta: { layout: 'apply-form-layout', noindex: true },
    component: NotFound
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  if (!to.meta.requiresAuth) {
    return next();
  }

  await store.dispatch('candidateModule/initAuth');

  if (store.getters['candidateModule/isAuthenticated']) {
    return next();
  }

  // Not signed in: send the candidate to Keycloak and stop this navigation.
  // After login they are redirected back to the cockpit.
  store.dispatch('candidateModule/login', window.location.href);
  return next(false);
});

router.afterEach((to, from) => {
  updateNoIndexTag(to);

  if (!to.meta.noTracking) {
    TrackingService.queuePageView(to, from);
  }
});

export default router;
